import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { tap, switchMap, of } from 'rxjs';
import { SubSink } from 'subsink';
import { jwtDecode } from 'jwt-decode';
import { select, Store } from '@ngrx/store';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { IJwtDto } from '@chiron/app/shared/dto/auth.dto';
import { selectQueryParams } from '@frontdoor/app/state/router.selectors';
import { SupportButtonComponent } from '@shared/lib/components/support-button/support-button.component';
import { RouterModule } from '@angular/router';
import { ResetPasswordService } from '@frontdoor/app/services/reset-password.service';

class PasswordValidator {
  static patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return error;
      }
      const valid = regex.test(control.value);
      return valid ? null : error;
    };
  }
  static matchValues(matchTo: FormControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = control.value;
      const matchToValue = matchTo.value;
      if (controlValue === matchToValue) {
        return null;
      } else {
        return { mismatch: true };
      }
    };
  }
}

@Component({
  selector: 'ba-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    SupportButtonComponent,
    RouterModule,
  ],
})
export class ChangePasswordComponent implements OnDestroy {
  private _subs = new SubSink();
  username: string | null = null;
  readonly isFirstTime = signal<boolean>(false);
  private _accessToken: string | null = null;
  readonly newPassword = new FormControl('', [
    Validators.required,
    Validators.minLength(8),
    PasswordValidator.patternValidator(new RegExp('(?=.*[0-9])'), {
      requiresDigit: true,
    }),
    PasswordValidator.patternValidator(new RegExp('(?=.*[A-Z])'), {
      requiresUppercase: true,
    }),
    PasswordValidator.patternValidator(new RegExp('(?=.*[a-z])'), {
      requiresLowercase: true,
    }),
    PasswordValidator.patternValidator(new RegExp('(?=.*[$@^!%*?&])'), {
      requiresSpecialChars: true,
    }),
  ]);
  readonly confirmPassword = new FormControl('', [
    Validators.required,
    PasswordValidator.matchValues(this.newPassword),
  ]);
  changePasswordForm: FormGroup = new FormGroup({
    newPassword: this.newPassword,
    confirmPassword: this.confirmPassword,
  });
  constructor(
    private readonly _store: Store,
    protected readonly _resetPasswordService: ResetPasswordService
  ) {
    this._subs.sink = this.newPassword?.valueChanges.subscribe(() => {
      this.confirmPassword?.updateValueAndValidity();
    });
    this._subs.sink = this._store
      .pipe(
        select(selectQueryParams),
        tap((params) => {
          if (params.jwt) {
            this._accessToken = params.jwt;
            const decoded = this._accessToken
              ? jwtDecode<IJwtDto>(this._accessToken)
              : null;
            this.username = decoded?.sub ?? null;
          }
        }),
        switchMap(() => {
          return this.username
            ? this._resetPasswordService
                .userHasSetPassword(this.username)
                .pipe(tap((hasSet) => this.isFirstTime.set(!hasSet)))
            : of(null);
        })
      )
      .subscribe();
  }
  get passwordValid() {
    return this.newPassword?.errors === null;
  }
  get requiredValid() {
    return !this.newPassword?.hasError('required');
  }
  get minLengthValid() {
    return !this.newPassword?.hasError('minlength') && this.requiredValid;
  }
  get requiresDigitValid() {
    return !this.newPassword?.hasError('requiresDigit');
  }
  get requiresUppercaseValid() {
    return !this.newPassword?.hasError('requiresUppercase');
  }
  get requiresLowercaseValid() {
    return !this.newPassword?.hasError('requiresLowercase');
  }
  get requiresSpecialCharsValid() {
    return !this.newPassword?.hasError('requiresSpecialChars');
  }

  async onSubmit() {
    this.changePasswordForm.markAllAsTouched();
    if (this.changePasswordForm.valid && this._accessToken) {
      this._subs.sink = this._resetPasswordService
        .changePassword(
          this.changePasswordForm.value.confirmPassword,
          this._accessToken,
          this.isFirstTime()
        )
        .subscribe();
    }
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
  }
}
